// Virtually every widget uses this. Include in the main chunk to avoid duplication.
require('./lib/BaseWidget');

/**
 * A factory to create widgets within the nucleus framework. Widgets can be registered
 * with the factory to allow custom implementations on a per widget type.
 */
export default class WidgetFactory {
  /**
   * A simple constructor which allows widgets to be registered with the factory.
   * If there are any duplicate widgets the last one present in the list will
   * be the one present in the factory.
   *
   * @param widgetRegistrationEntries - The widgets to be registered with the factory.
   * @param { sectionRegistrationEntries, loadWidgetResources } - Optional Parameters
   *   sectionRegistrationEntries - The sections to be registered with the factory.
   *   loadWidgetResources - Function that allows project to customize the resource loading behavior for the widgets
   *      per widget type.
   */
  constructor(widgetRegistrationEntries = [], { sectionRegistrationEntries, loadWidgetResources } = {}) {
    this.metadataByWidgetType = {};
    this.creatorsByWidgetType = {};
    this.metadataBySectionType = {};
    this.internalLoadWidgetResources = loadWidgetResources || defaultLoadWidgetResources(this);
    widgetRegistrationEntries.forEach((entry) => {
      this.metadataByWidgetType[entry.metadata.type] = entry.metadata;
      this.creatorsByWidgetType[entry.metadata.type] = entry.creator;
    });
    if (sectionRegistrationEntries) {
      sectionRegistrationEntries.forEach(entry => {
        this.metadataBySectionType[entry.metadata.type] = entry.metadata;
      });
    }
  }

  /**
   * Creates a promise that will resolve with the specified widget. If the widget
   * does not exist a default empty widget will be returned.
   *
   * @param widgetType - The type of widget to create.
   * @returns A promise that will resolver into the widget. If the widget is not
   * registered with the factory then it will return an empty cell widget.
   */
  loadComponent(widgetType): any {
    const widgetCreator = this.creatorsByWidgetType[widgetType];
    if (!widgetCreator) {
      throw new Error(`Undefined Widget Type '${widgetType}' Encountered!`);
    }
    return Promise.resolve(widgetCreator());
  }

  /**
   * Returns the metadata for all widgets registered with the factory.
   * @returns The widget metadata for each widget registered in the factory.
   */
  getAllMetaData() {
    return this.metadataByWidgetType;
  }

  /**
   * Returns the metadata for the specific widget.
   * @param widgetType - The type of widget to return the metadata.
   * @returns The widget metadata for the widget. Throws an error if the type is not registered in the factory.
   */
  loadMetaData(widgetType) {
    const widgetMetdata = this.metadataByWidgetType[widgetType];
    if (!widgetMetdata) {
      throw new Error(`Undefined Widget Type '${widgetType}' Encountered!`);
    }
    return widgetMetdata;
  }

  /**
   * Returns the metadata for all sections registered with the factory.
   * @returns The section metadata for each section registered in the factory.
   */
  getAllSectionMetaData() {
    return this.metadataBySectionType;
  }

  /**
   * Returns the metadata for the specific section.
   * @param sectionType - The type of section to return the metadata.
   * @returns The seciton metadata for the section. Returns undefined if the type is not registered in the factory
   *    because registering section types is optional, its only necessary if an app wants to take advantage of any
   *    of the additonal functionality of defining section metadata.
   */
  loadSectionMetaData(sectionType) {
    const sectionMetadata = this.metadataBySectionType[sectionType];
    return sectionMetadata;
  }

  /**
   * Creates the promises that will resolve when customized (per widget type) widget resources are loaded per page.
   * @param widgets - Widget configs for which the resources need to be loaded.
   */
  loadWidgetResources(widgets = []) {
    return async (dispatch) => {
      const widgetTypes = Object.keys(widgets.reduce((types, widget) => ({ ...types, [widget.widgetType]: true }), {}));
      await Promise.all([
        ...widgetTypes.map(widgetType => this.loadComponent(widgetType)),
        dispatch(this.internalLoadWidgetResources(widgets, widgetTypes))
      ]);
    };
  }
}

/**
 * Creates the promises that will resolve when default widget resources are loaded per page.
 * @param widgetFactory - widgetFactory to load the resources from widget metadata.
 */
function defaultLoadWidgetResources(widgetFactory) {
  return (widgets, widgetTypes) => {
    return async (dispatch) => {
      const loadWidgetTypeResources = (widgetType) => {
        const widgetMetadata = widgetFactory.loadMetaData(widgetType);
        return widgetMetadata.loadResources ? dispatch(widgetMetadata.loadResources()) : Promise.resolve();
      };
      await Promise.all(widgetTypes.map(loadWidgetTypeResources));
    };
  };
}
